/**
 * Reviews Hook
 * React hook for fetching and managing product reviews
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewService } from '@/infrastructure/config/services.config';
import { ReviewFilters, CreateReviewData } from '@/domain/entities/review.entity';

export interface UseReviewsOptions {
  limit?: number;
  offset?: number;
  filters?: ReviewFilters;
  includeStats?: boolean;
  enabled?: boolean;
}

export function useReviews(productId: string, options: UseReviewsOptions = {}) {
  const { limit = 10, offset = 0, filters, includeStats = true, enabled = true } = options;

  return useQuery({
    queryKey: ['reviews', productId, limit, offset, filters, includeStats],
    queryFn: () => reviewService.getReviewsByProductId(productId, {
      limit,
      offset,
      filters,
      includeStats,
    }),
    enabled: enabled && !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useReviewStats(productId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['review-stats', productId],
    queryFn: () => reviewService.getReviewStats(productId),
    enabled: enabled && !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useVoteReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, voteType }: { reviewId: string; voteType: 'helpful' | 'not_helpful' }) =>
      reviewService.voteReview(reviewId, voteType),
    onSuccess: () => {
      // Invalidate reviews queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
  });
}

export function useCreateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReviewData) => reviewService.createReview(data),
    onSuccess: (_, variables) => {
      // Invalidate reviews for the specific product
      queryClient.invalidateQueries({ queryKey: ['reviews', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['review-stats', variables.productId] });
    },
  });
}

